import { faEllipsisVertical, faEye, faBan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RateStatusBadge } from '../components/display/RateStatusBadge';
import { Rate } from '../models/rate.types';

interface UseAgGridColumnsProps {
  onDeactivate: (rate: Rate) => void;
  canManage: boolean;
}

export const useAgGridColumns = ({ onDeactivate, canManage }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('ratesModule');

  const ActionsCellRenderer = useCallback(
    ({ data }: { data: Rate }) => {
      if (!data) return null;

      const isActive = data.is_active;

      return (
        <div className="flex items-center justify-center h-full">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="hover:bg-muted rounded-md h-8 w-8 flex items-center justify-center">
                <FontAwesomeIcon icon={faEllipsisVertical} className="text-icons" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44 border-card-border">
              <DropdownMenuLinkItem to={`/invoices/rates/${data.id}`}>
                <FontAwesomeIcon icon={faEye} className="mr-2 w-3.5" />
                {t('table.actions.view')}
              </DropdownMenuLinkItem>
              {canManage && isActive && (
                <DropdownMenuItem onClick={() => onDeactivate(data)} className="text-error">
                  <FontAwesomeIcon icon={faBan} className="mr-2 w-3.5" />
                  {t('table.actions.deactivate')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
    [onDeactivate, canManage, t],
  );

  const StatusCellRenderer = useCallback(({ data }: { data: Rate }) => {
    if (!data) return null;
    return <RateStatusBadge isActive={data.is_active} />;
  }, []);

  const NameCellRenderer = useCallback(({ data }: { data: Rate }) => {
    if (!data) return null;
    return <span>{data.name}</span>;
  }, []);

  // const DateCellRenderer = useCallback(({ data }: { data: Rate }) => {
  //   if (!data?.created_at) return null;
  //   return <span>{formatDate(data.created_at)}</span>;
  // }, []);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: t('table.name'),
        field: 'name',
        minWidth: 200,
        cellRenderer: NameCellRenderer,
      },
      {
        headerName: t('table.status'),
        field: 'is_active',
        minWidth: 120,
        cellRenderer: StatusCellRenderer,
      },
      // Waiting for backend
      // {
      //   headerName: t('table.createdAt'),
      //   field: 'created_at',
      //   flex: 1,
      //   minWidth: 150,
      //   cellRenderer: DateCellRenderer,
      // },
      {
        headerName: t('table.actions.title'),
        field: 'actions',
        width: 80,
        maxWidth: 80,
        pinned: 'right',
        cellRenderer: ActionsCellRenderer,
        sortable: false,
        filter: false,
      },
    ],
    [t, NameCellRenderer, StatusCellRenderer, ActionsCellRenderer],
  );

  return { columnDefs };
};
